import get from 'lodash.get';

// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatTimeConflictError from 'utils/format-time-conflict-error';

// Types
import { Namespace, Namespaces } from 'types';
import { AppState } from 'services/types';
import { ErrorDetailsState, ErrorState, ErrorProps } from './types';

const selectErrors = (state: AppState): ErrorState => state.error;
const selectNamespace = (
    _state: AppState,
    props: ErrorProps
): Namespace | Namespaces | undefined => props.namespace;
const selectUuid = (_state: AppState, props: ErrorProps): string | undefined =>
    props.uuid;

// TODO refactor makeSelectError into makeSelectErrorMessage
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectError = (namespace?: Namespace | Namespaces) =>
    createDeepEqualSelector(
        [selectErrors, selectNamespace],
        (errorsState, namespaceState): string | undefined => {
            const namespaceToUse = namespaceState || namespace;

            if (!namespaceToUse) {
                return undefined;
            }

            // Return the messages for multipul errors
            if (Array.isArray(namespaceToUse)) {
                const message = namespaceToUse
                    .map((value: string): string | undefined =>
                        get(errorsState, [value, 'message'])
                    )
                    .filter((value: string | undefined): boolean => !!value)
                    .join(', ');

                return message || undefined;
            }

            // Return the message for a single error
            return get(errorsState, [namespaceToUse, 'message']);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectErrorDetailsMessages = () =>
    createDeepEqualSelector(
        [selectErrors, selectNamespace],
        (errorsState: ErrorState, namespaceState): string[] | undefined => {
            if (!namespaceState) {
                return undefined;
            }

            // Currently don't support an array of namespace values
            if (!Array.isArray(namespaceState)) {
                // Get the errors details
                const errorDetails = get(errorsState, [
                    namespaceState,
                    'details'
                ]);
                // Extract the messages as an array of strings
                if (errorDetails) {
                    return errorDetails.map(
                        (value: ErrorDetailsState): string | undefined =>
                            get(value, 'message')
                    );
                }
            }

            return undefined;
        }
    );

const selectNestedErrorsByUuid = createDeepEqualSelector(
    [selectErrors, selectNamespace],
    (
        errorsState: ErrorState,
        namespaceState
    ): Record<string, string> | undefined => {
        if (!namespaceState) {
            return undefined;
        }

        // Currently don't support an array of namespace values
        if (!Array.isArray(namespaceState)) {
            // Get the error's errors
            const errorErrors = get(errorsState, [namespaceState, 'errors']);

            return (
                errorErrors &&
                errorErrors.reduce((accumulator: any, currentValue: any) => {
                    if (currentValue.details) {
                        currentValue.details.forEach((detail: any) => {
                            const uuid = get(detail, [0, 'uuid']);
                            const { error } = currentValue;
                            let customErrorMessage;

                            if (error === 'time-conflict') {
                                customErrorMessage = formatTimeConflictError(
                                    detail[1]
                                );
                            }

                            if (uuid) {
                                accumulator[uuid] = customErrorMessage || error;
                            }
                        });
                    }
                    if (currentValue.uuids) {
                        currentValue.uuids.forEach((uuid: string) => {
                            accumulator[uuid] = currentValue.error;
                        });
                    }
                    return accumulator;
                }, {})
            );
        }

        return undefined;
    }
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectNestedErrorsExist = () =>
    createDeepEqualSelector(
        selectNestedErrorsByUuid,
        (nestedErrorsByUuidState): boolean => !!nestedErrorsByUuidState
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectNestedErrorUuids = () =>
    createDeepEqualSelector(
        selectNestedErrorsByUuid,
        (nestedErrorsByUuidState): string[] | undefined => {
            if (nestedErrorsByUuidState) {
                return Object.keys(nestedErrorsByUuidState);
            }
            return undefined;
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectNestedErrorExists = () =>
    createDeepEqualSelector(
        [selectNestedErrorsByUuid, selectUuid],
        (nestedErrorsByUuidState, uuidProp): boolean => {
            if (!uuidProp) {
                return false;
            }

            return !!get(nestedErrorsByUuidState, uuidProp);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectNestedError = () =>
    createDeepEqualSelector(
        [selectNestedErrorsByUuid, selectUuid],
        (nestedErrorsByUuidState, uuidProp): string | undefined => {
            if (!uuidProp) {
                return undefined;
            }

            return get(nestedErrorsByUuidState, uuidProp);
        }
    );

export {
    selectErrors,
    makeSelectError,
    makeSelectErrorDetailsMessages,
    selectNestedErrorsByUuid,
    makeSelectNestedErrorsExist,
    makeSelectNestedErrorUuids,
    makeSelectNestedErrorExists,
    makeSelectNestedError
};
